import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from 'axios'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import Avatar from '../ui/Avatar'
import VideoModal from '../ui/VideoModal'
import PlayerCard from '../ui/PlayerCard'
import './challenges.css'

/*
 * ONE CHALLENGE — the brief, the leaderboard, and the tape.
 *
 * POST manager/dashboard/challengeusers  { ChallengeUUID, Countries }
 *   -> [{ ChallengeUUID, Title, Descr, FileID, Points,
 *         UserUUID, FirstName, LastName }]
 *
 * One request answers two questions, so the page answers both. The head of the
 * payload describes the challenge; every row is a submission. Rows are ranked by
 * points because that is the only order a scout reads a challenge in — who won
 * it — and the number is repeated as a share-of-leader bar so the column reads
 * as a shape rather than as a dozen loose integers.
 *
 * THE DIALOG is a scouting view, not a video dump. The clips are phone video:
 * portrait. The old landscape box wrapped a 9:16 clip in two columns of black,
 * so the frame is now derived from the clip's own ratio and bounded by the
 * viewport (see .chl-stage). It also names the player and their score — the one
 * thing the old modal never said — and lets the scout walk the whole field of
 * submissions without closing and reopening it once per player.
 *
 * The request itself — URL, payload, headers, and what is read out of the
 * response — is untouched.
 */

/* Portrait-first. The height is what the viewport can spare; the width follows
   from 9:16. On a short or narrow window the width becomes the binding
   constraint instead and the height follows from it, so the frame always fits
   whole and never needs the page to scroll to it. */

const pointsOf = (row) => {
  const n = Number(row && row.Points)
  return Number.isFinite(n) ? n : 0
}

const nameOf = (row) => {
  if (!row) return 'Unknown player'
  const joined = [row.FirstName, row.LastName].filter(Boolean).join(' ').trim()
  const explicit = row.User ? String(row.User).trim() : ''
  return joined || explicit || 'Unknown player'
}

export default function Challenges() {
  const { challengeuuid } = useParams()
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [title, setTitle] = useState('')
  const [descr, setDescr] = useState('')

  /* -1 = closed. The dialog's whole state is "which clip am I on", which is
     what makes prev/next a one-line move instead of a second open/close cycle. */
  const [clipIndex, setClipIndex] = useState(-1)
  const [artBroken, setArtBroken] = useState(false)

  /* The row control that opened the dialog, so focus can go back to it. */
  const triggerRef = useRef(null)

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadData = () => {
    setFailed(false)
    const obj = {
      ChallengeUUID: challengeuuid,
      Countries: JSON.parse(sessionStorage.getItem('appCountries'))
    }
    axios.post(process.env.REACT_APP_API_SERVER + 'manager/dashboard/challengeusers', obj, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken")
      }
    })
      .then(res => {
        setData(res.data)
        /* Same two fields, read the same way — guarded only so that a challenge
           with no submissions yet reaches the empty state instead of throwing
           on res.data[0] and landing in the error branch. */
        const first = Array.isArray(res.data) ? res.data[0] : null
        setTitle(first && first.Title ? first.Title : '')
        setDescr(first && first.Descr ? first.Descr : '')
        setIsLoading(false)
      }).catch(err => {
        setFailed(true)
        setIsLoading(false)

      })
  }

  /* Render-side guards only — nothing here changes what the request reads.
     `Descr` arrives null on challenges with no write-up, and `.replace` on
     null threw during render, taking the whole page down. */
  const head = Array.isArray(data) ? data[0] : null
  const headUUID = head && head.ChallengeUUID ? head.ChallengeUUID : ''
  const asHtml = (value) => ({ __html: String(value == null ? '' : value).replace(/\n/g, '<br>') })

  /* Ranked once, and everything downstream — the list, the meters, the dialog's
     prev/next order — reads from this one array. Equal scores share a rank and
     keep the order the server sent them in. */
  const board = useMemo(() => {
    const rows = Array.isArray(data) ? data.slice() : []

    const sorted = rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => (pointsOf(b.row) - pointsOf(a.row)) || (a.index - b.index))
      .map((item) => item.row)

    let rank = 0
    let previous = null

    return sorted.map((row, index) => {
      const points = pointsOf(row)
      if (previous === null || points !== previous) rank = index + 1
      previous = points

      return {
        key: (row.UserUUID || row.FileID || 'row') + '-' + index,
        row,
        rank,
        uuid: row.UserUUID,
        name: nameOf(row),
        position: row.Position,
        points,
        fileID: row.FileID
      }
    })
  }, [data])

  const clips = useMemo(() => board.filter((entry) => !!entry.fileID), [board])

  const topScore = board.length > 0 ? board[0].points : 0
  const average = board.length > 0
    ? Math.round(board.reduce((sum, entry) => sum + entry.points, 0) / board.length)
    : 0

  const active = clipIndex >= 0 && clipIndex < clips.length ? clips[clipIndex] : null
  const open = !!active

  const handleClose = useCallback(() => {
    setClipIndex(-1)
  }, [])

  const handleShow = (entry, event) => {
    const index = clips.indexOf(entry)
    if (index === -1) return
    triggerRef.current = event && event.currentTarget ? event.currentTarget : null
    setClipIndex(index)
  }

  const step = useCallback((delta) => {
    setClipIndex((current) => {
      const next = current + delta
      return next >= 0 && next < clips.length ? next : current
    })
  }, [clips.length])

  /* A reload that shortens the list must not strand the dialog on a row that
     no longer exists. */
  useEffect(() => {
    if (clipIndex >= clips.length) setClipIndex(-1)
  }, [clips.length, clipIndex])

  /* Escape, arrows and the scroll lock live in VideoModal now. */



  /* Closing returns the scout to the exact row they left from. */
  useEffect(() => {
    if (open) return
    const node = triggerRef.current
    triggerRef.current = null
    if (node && typeof node.focus === 'function') node.focus()
  }, [open])

  const playGlyph = (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5.2v13.6L19 12 8 5.2z" />
    </svg>
  )

  const rows = board.map((entry) => {
    const share = topScore > 0 ? Math.max(0, Math.min(100, (entry.points / topScore) * 100)) : 0

    return (
      <li key={entry.key}>
        <div className="chl-row">
          <span className={entry.rank <= 3 ? 'chl-rank chl-rank-top' : 'chl-rank'}>
            {entry.rank}
          </span>

          <PlayerCard
            uuid={entry.uuid}
            name={entry.name}
            position={entry.position}
            points={entry.points}
          >
            {entry.uuid
              ? (
                <Link className="chl-face" to={`/user/${entry.uuid}`} aria-label={`Open ${entry.name}`}>
                  <Avatar uuid={entry.uuid} name={entry.name} size={38} />
                </Link>
              )
              : <Avatar name={entry.name} size={38} />}
          </PlayerCard>

          <span className="chl-who">
            {entry.uuid
              ? <Link className="chl-name" to={`/user/${entry.uuid}`}>{entry.name}</Link>
              : <span className="chl-name">{entry.name}</span>}
            <span className="chl-sub">
              {entry.fileID ? 'Video submitted' : 'No video submitted'}
            </span>
          </span>

          <span className="chl-meter-cell">
            <span
              className="chl-meter"
              role="img"
              aria-label={`${entry.points} of ${topScore} points`}
            >
              <span className="chl-meter-fill" style={{ width: share + '%' }} />
            </span>
          </span>

          <span className="chl-points num">
            {entry.points}
            <small>pts</small>
          </span>

          <span className="chl-act">
            <Btn
              onClick={(event) => handleShow(entry, event)}
              disabled={!entry.fileID}
              title={entry.fileID ? `Watch ${entry.name}` : 'No video on this submission'}
              aria-label={`Watch ${entry.name}`}
            >
              {playGlyph}
              <span className="chl-act-label">Watch</span>
            </Btn>
          </span>
        </div>
      </li>
    )
  })

  const dialog = open ? (
    <VideoModal
      items={clips.map((entry) => ({
        fileId: entry.fileID,
        name: entry.name,
        uuid: entry.uuid,
        score: entry.points,
        scoreLabel: 'pts',
        subtitle: `Rank ${entry.rank} of ${board.length}${title ? ' · ' + title : ''}`,
      }))}
      index={clipIndex}
      title={title}
      onIndex={setClipIndex}
      onClose={handleClose}
    />
  ) : null

  return (
    <div className="chl-canvas">

      <Panel>
        {isLoading && (
          <div className="chl-sk">
            <div className="skeleton chl-sk-line"></div>
            <div className="skeleton chl-sk-line chl-sk-short"></div>
          </div>
        )}

        {!isLoading && (
          <div className="chl-brief">
            {headUUID && !artBroken
              ? (
                <img
                  className="chl-art"
                  src={`${process.env.REACT_APP_API_SERVER}/admin/files/${headUUID}/123`}
                  alt=""
                  onError={() => setArtBroken(true)}
                />
              )
              : (
                <span className="chl-art-blank" aria-hidden="true">
                  {(title || 'C').trim().charAt(0).toUpperCase()}
                </span>
              )}

            <div className="chl-brief-text">
              <span className="mono chl-overline">Challenge</span>
              <h2 className="chl-title">{title || 'Challenge'}</h2>

              {descr
                ? <div className="chl-descr" dangerouslySetInnerHTML={asHtml(descr)} />
                : <div className="chl-descr">No brief was written for this challenge.</div>}

              {!failed && <ul className="chl-facts">
                <li className="chl-fact">
                  <span className="mono chl-fact-label">Submissions</span>
                  <span className="chl-fact-value num">{board.length}</span>
                </li>
                <li className="chl-fact">
                  <span className="mono chl-fact-label">Top score</span>
                  <span className="chl-fact-value num">{topScore}</span>
                </li>
                <li className="chl-fact">
                  <span className="mono chl-fact-label">Average</span>
                  <span className="chl-fact-value num">{average}</span>
                </li>
                <li className="chl-fact">
                  <span className="mono chl-fact-label">With video</span>
                  <span className="chl-fact-value num">{clips.length}</span>
                </li>
              </ul>}

              {!failed && (
                <p className="chl-legend">
                  <span className="chl-legend-key" aria-hidden="true"></span>
                  Points are the score each player earned on this challenge. The board is ranked by
                  that score, and the bar on every row is the player's share of the leading score.
                </p>
              )}
            </div>
          </div>
        )}
      </Panel>

      <Panel
        title="Leaderboard"
        subtitle={!isLoading && board.length > 0
          ? `${board.length} ${board.length === 1 ? 'submission' : 'submissions'} · ${clips.length} with video`
          : 'Ranked by points'}
      >
        {isLoading && (
          <div className="chl-sk">
            <div className="skeleton chl-sk-line"></div>
            <div className="skeleton chl-sk-line"></div>
            <div className="skeleton chl-sk-line chl-sk-short"></div>
          </div>
        )}

        {!isLoading && failed && (
          <div className="chl-alert" role="alert">
            <b>This challenge could not be loaded.</b>
            <span>The server did not answer. Nothing has been lost — try again.</span>
            <Btn onClick={() => { setIsLoading(true); loadData() }}>Try again</Btn>
          </div>
        )}

        {!isLoading && !failed && board.length === 0 && (
          <div className="chl-blank">
            <span className="chl-blank-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M4 5.5h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1zm6 3v7l6-3.5-6-3.5z" />
              </svg>
            </span>
            <h2 className="chl-blank-title">No one has entered yet</h2>
            <p className="chl-blank-text">
              Nobody in the countries you cover has submitted a video for this challenge.
              As soon as a player enters, their attempt is ranked here and their tape becomes
              watchable from this page.
            </p>
          </div>
        )}

        {!isLoading && !failed && board.length > 0 && (
          <div className="scroll-body chl-scroll">
            <div className="chl-head">
              <span className="mono chl-head-right">#</span>
              <span className="mono"></span>
              <span className="mono">Player</span>
              <span className="mono chl-head-meter">Share of leader</span>
              <span className="mono chl-head-right">Points</span>
              <span className="mono chl-head-right">Video</span>
            </div>

            <ol className="chl-board">{rows}</ol>
          </div>
        )}
      </Panel>

      {dialog}
    </div>
  )
}
